<template>
  <div class="popover-wrapper">
    <div
      ref="triggerRef"
      class="popover-trigger"
      @click="togglePopover"
    >
      <slot name="trigger" />
    </div>

    <Teleport to="body">
      <div
        v-if="isVisible"
        ref="popoverRef"
        :style="popoverStyles"
      >
        <slot />
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from "vue";
import { onClickOutside } from "@vueuse/core";
const props = defineProps({
  offsetTop: { type: Number, required: false, default: 6 }
});
const emit = defineEmits(["show", "hide"]);
const triggerRef = ref(null);
const popoverRef = ref(null);
const isVisible = ref(false);
const popoverPosition = ref({ top: 0, left: 0 });
let resizeObserver = null;
let mutationObserver = null;
const popoverStyles = computed(() => ({
  position: "absolute",
  top: `${popoverPosition.value.top}px`,
  left: `${popoverPosition.value.left}px`,
  zIndex: 1e3
}));
onMounted(() => {
  setupObservers();
});
onUnmounted(() => {
  cleanupObservers();
});
onClickOutside(popoverRef, () => {
  if (isVisible.value) {
    hidePopover();
  }
});
function togglePopover() {
  if (isVisible.value) {
    hidePopover();
  } else {
    showPopover();
  }
}
async function showPopover() {
  isVisible.value = true;
  emit("show");
  await nextTick();
  updatePosition();
}
function hidePopover() {
  isVisible.value = false;
  emit("hide");
}
function updatePosition() {
  if (!triggerRef.value || !isVisible.value) return;
  const triggerRect = triggerRef.value.getBoundingClientRect();
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
  popoverPosition.value = {
    top: triggerRect.bottom + scrollTop + props.offsetTop,
    left: triggerRect.left + scrollLeft - (popoverRef.value?.clientWidth ?? 0) + (triggerRef.value?.clientWidth ?? 0)
  };
}
function setupObservers() {
  if (!triggerRef.value) return;
  resizeObserver = new ResizeObserver(() => {
    if (isVisible.value) {
      updatePosition();
    }
  });
  resizeObserver.observe(triggerRef.value);
  mutationObserver = new MutationObserver(() => {
    if (isVisible.value) {
      updatePosition();
    }
  });
  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["style", "class"]
  });
  window.addEventListener("scroll", updatePosition, true);
  window.addEventListener("resize", updatePosition);
}
function cleanupObservers() {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
  if (mutationObserver) {
    mutationObserver.disconnect();
    mutationObserver = null;
  }
  window.removeEventListener("scroll", updatePosition, true);
  window.removeEventListener("resize", updatePosition);
}
defineExpose({
  show: showPopover,
  hide: hidePopover,
  toggle: togglePopover,
  isVisible: computed(() => isVisible.value)
});
</script>

<style scoped>
.popover-wrapper{position:relative}.popover-trigger{cursor:pointer}
</style>
